// stateMachine — duration-tracking state machine shared by all detectors.
//
// Most detection signals are NOT "fire once on a frame" — they're "fire
// only after the condition has been true for N seconds". A single frame
// showing 2 faces could be a sibling walking by; 2 faces for 1+ second
// is a real cheat. This module wraps that logic so each detector doesn't
// re-implement timing/debouncing.
//
// Usage:
//   const sm = createDurationStateMachine({
//     warnAfterMs: 3000,
//     flagAfterMs: 10000,
//     onWarn:  () => emit({...}),
//     onFlag:  () => emit({...}),
//     onClear: () => emit({...}),  // optional
//   });
//   // For every frame:
//   sm.update(condition_is_true_right_now);
//
// State diagram:
//   IDLE → ACTIVE_BRIEF → ACTIVE_WARNED → ACTIVE_FLAGGED
//      ↑__________|_____________|______________|
//   (any time condition becomes false → IDLE + onClear)

const STATES = Object.freeze({
  IDLE: 'idle',                  // condition not active
  ACTIVE_BRIEF: 'active_brief',  // condition active, not yet at warn threshold
  ACTIVE_WARNED: 'active_warned', // warned but not flagged
  ACTIVE_FLAGGED: 'active_flagged', // flagged (capped — won't re-flag)
});

/**
 * Create a state machine that fires onWarn / onFlag once per "episode"
 * (an episode = the condition staying true continuously).
 *
 * @param {object} opts
 * @param {number} opts.warnAfterMs        Fire onWarn when condition has been
 *                                          true continuously for this long.
 * @param {number} opts.flagAfterMs        Fire onFlag at this point. Must be
 *                                          >= warnAfterMs. Set to Infinity to
 *                                          skip the flag stage.
 * @param {() => void} opts.onWarn         Fired once per episode at warn.
 * @param {() => void} opts.onFlag         Fired once per episode at flag.
 * @param {() => void} [opts.onClear]      Fired when the episode ends
 *                                          (condition flips false).
 * @returns {{ update: (active: boolean) => string, reset: () => void, state: () => string }}
 */
export function createDurationStateMachine({
  warnAfterMs = 3000,
  flagAfterMs = 10000,
  onWarn = () => {},
  onFlag = () => {},
  onClear = () => {},
} = {}) {
  if (flagAfterMs < warnAfterMs) {
    // Defensive: keep monotonic order. Otherwise we'd fire flag before warn.
    flagAfterMs = warnAfterMs;
  }

  let state = STATES.IDLE;
  let activeSince = 0;

  const reset = () => {
    if (state !== STATES.IDLE) {
      onClear();
    }
    state = STATES.IDLE;
    activeSince = 0;
  };

  /**
   * Call this on every detector tick.
   * @returns the new state, useful for logging.
   */
  const update = (active) => {
    const now = Date.now();

    if (!active) {
      if (state !== STATES.IDLE) {
        reset();
      }
      return state;
    }

    // active === true
    if (state === STATES.IDLE) {
      state = STATES.ACTIVE_BRIEF;
      activeSince = now;
      return state;
    }

    const elapsed = now - activeSince;

    if (state === STATES.ACTIVE_BRIEF && elapsed >= warnAfterMs) {
      state = STATES.ACTIVE_WARNED;
      try { onWarn(); } catch (_) { /* keep ticking */ }
    }
    if (state === STATES.ACTIVE_WARNED && elapsed >= flagAfterMs) {
      state = STATES.ACTIVE_FLAGGED;
      try { onFlag(); } catch (_) { /* keep ticking */ }
    }

    return state;
  };

  return {
    update,
    reset,
    state: () => state,
  };
}

export { STATES };
